// @owners { team: patients }
import { getIsOrderBundling } from '~shared/features/checkout/selectors/getCheckoutFlow';
import { getOrderAddressId } from '~shared/features/checkout/selectors/getOrder';
import { getIsEditingExistingOrder } from '~shared/features/ui/selectors/getCart';
import { type OrderPricingQueryData } from '~shared/queries/useOrderPricing';
import { useSelectorShared } from '~shared/store';
import { getOrderBundlingCheckoutOrigin, useOrderBundlingOptions } from '../../order-bundling';

type UseGetCheckoutTotalPriceArgs = {
  /** From `useOrderPricing()` — pass through so this hook does not subscribe to pricing again. */
  orderPricing: OrderPricingQueryData | undefined;
};

// returns the total price at checkout
// uses orderPricing if not in order bundling
// uses earliestUpcomingShipment and cartShipment if in order bundling
export const useGetCheckoutTotalPrice = ({ orderPricing }: UseGetCheckoutTotalPriceArgs) => {
  const addressID = useSelectorShared(getOrderAddressId);
  const isEditingOrder = useSelectorShared(getIsEditingExistingOrder);
  const isOrderBundling = useSelectorShared(getIsOrderBundling);
  const orderBundlingOrigin = getOrderBundlingCheckoutOrigin({ isEditingOrder });
  const { earliestUpcomingShipment, cartShipment } = useOrderBundlingOptions({
    address_id: addressID,
    origin: orderBundlingOrigin,
  });

  const nonBundlingTotalPrice =
    orderPricing?.total?.info_key === 'price_unavailable' ? undefined : (orderPricing?.total?.value ?? undefined);

  const originalPrice = earliestUpcomingShipment?.price ?? undefined;
  const isOriginalPriceNumber = typeof originalPrice === 'number';

  const newPrice = cartShipment?.price ?? undefined;
  const isNewPriceNumber = typeof newPrice === 'number';

  let bundlingTotalPrice;
  if (isOriginalPriceNumber && isNewPriceNumber) {
    bundlingTotalPrice = originalPrice + newPrice;
  } else {
    bundlingTotalPrice = undefined;
  }

  if (isOrderBundling) {
    return bundlingTotalPrice;
  } else {
    return nonBundlingTotalPrice;
  }
};
